import type { ReactNode } from 'react'
import { useRef } from 'react'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

export interface MoveToNewRfqConfirmModalProps {
  readonly open: boolean
  /** The name of the part about to be moved — drives the body copy's
   * quoted "{name}" (design `.dc.html` L4472-4473, `moveRfqModal.name`). */
  readonly partName: string
  readonly onCancel: () => void
  readonly onConfirm: () => void
  /**
   * UID-325 (Gate 5) — overrides the body copy.
   *
   * The default copy asserts the move WILL happen ("will be removed from this
   * request and started as its own RFQ. You'll be taken to the new request"),
   * which is true for the original `RfqReviewPage` caller, where the lines are
   * a local draft and the move is real. The RFQ DETAIL page's row menu reuses
   * this dialog, but there the move cannot happen — no endpoint moves a line
   * out of an already-submitted RFQ — so that caller must not make the promise
   * and then report "nothing was moved". Gate 5 flagged the mismatch: honest
   * post-action toasts don't help if the PRE-action copy is the falsehood.
   *
   * Omitted by the review-page caller, which keeps the default verbatim.
   */
  readonly body?: ReactNode
}

/**
 * UID-262 — the row menu's "Move to a new RFQ" confirm modal ("Move to a new
 * RFQ?", design source of truth `.dc.html` L4469-4476, `moveRfqModal` /
 * `confirmMoveRfqNew`). Confirming removes the part from the CURRENT request,
 * creates a new RFQ *draft* containing it (no submit), and navigates to that
 * new draft's review page — see `RfqReviewPage.handleMoveToNewRfq` and
 * ADR-0088.
 *
 * Built directly on the shared `Modal.tsx` shell — NOT `ConfirmDialog.tsx` —
 * following the exact precedent `BulkAddPricedConfirmModal` set (ADR-0087):
 * `ConfirmDialog` hardcodes a red `variant="danger"` confirm button, but this
 * action is NON-destructive (`.dc.html`'s confirm button is the brand
 * `fx-btn-primary`/`--fictiv-teal`, not a destructive red) — the part is not
 * deleted, it is relocated to its own request the user is taken straight to.
 *
 * Token mapping (UID-260 / PR #229 precedent): `.dc.html`'s `--fictiv-teal*`
 * tokens do not exist in this repo. The confirm button's `--fictiv-teal`
 * background resolves to the BRAND token via `variant="primary"`
 * (`--color-brand-primary` #0F208B, 13.14:1 on white — the same mapping
 * `BulkAddPricedConfirmModal`'s Gate 5 W1 fix locked in), and the icon chip's
 * teal fill maps to the brand-tinted `--color-info-*` pair.
 */
export function MoveToNewRfqConfirmModal({
  open,
  partName,
  onCancel,
  onConfirm,
  body,
}: MoveToNewRfqConfirmModalProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Move to a new RFQ?"
      maxWidth={440}
      className="rfq-move-confirm-modal"
      initialFocusRef={cancelButtonRef}
      headerContent={
        <div className="rfq-move-confirm-header">
          <span className="rfq-move-confirm-icon-chip" aria-hidden="true">
            <IconMoveToNewRfq />
          </span>
          <h2 className="modal-title">Move to a new RFQ?</h2>
        </div>
      }
      footer={
        <>
          <Button ref={cancelButtonRef} variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Move to new RFQ
          </Button>
        </>
      }
    >
      <p className="rfq-move-confirm-body">
        {body ?? (
          <>
            <strong>&ldquo;{partName}&rdquo;</strong> will be removed from this request and started
            as its own RFQ. You&rsquo;ll be taken to the new request.
          </>
        )}
      </p>
    </Modal>
  )
}

/** The `.dc.html` move modal's icon (L4472) — an "open in a new place" glyph.
 * Inlined here (like `PartsReviewTable`'s local `KebabIcon`) rather than added
 * to the shared icon set, since it has exactly one caller. */
function IconMoveToNewRfq() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3h4v4M14 10l7-7M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </svg>
  )
}
